// src/components/ChatProvider.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// src/context/ChatContext.tsx
import { createContext, useContext } from "react";
var ChatContext = createContext(void 0);
var useChatContext = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return ctx;
};

// src/components/ChatProvider.tsx
import { jsx } from "react/jsx-runtime";
var DEFAULT_STORAGE_KEY = "sparkle:chat";
var DEFAULT_MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
var LOG_PREFIX = "[SparkleChat]";
var SUPPORTS_STORAGE = (() => {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch (e) {
    return false;
  }
})();
var log = (...args) => {
  if (typeof console !== "undefined") {
    console.info(LOG_PREFIX, ...args);
  }
};
var previewText = (value, max = 160) => {
  const raw = typeof value === "string" ? value : "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}\u2026`;
};
var ChatProvider = ({
  tenantId,
  endpoint,
  roomId,
  authStrategy = "guest",
  connectBehavior = "eager",
  idleDisconnectMs,
  getJwt,
  uploadFile,
  uploadAccept,
  maxUploadBytes = DEFAULT_MAX_UPLOAD_BYTES,
  fetchOptions,
  storageKey = DEFAULT_STORAGE_KEY,
  displayName,
  role,
  children,
  disabled
}) => {
  const baseEndpoint = useMemo(() => (endpoint != null ? endpoint : "").replace(/\/$/, ""), [endpoint]);
  const guestCacheKey = useMemo(() => `${storageKey}:guest:${tenantId}`, [storageKey, tenantId]);
  const userCachePrefix = useMemo(() => `${storageKey}:user:${tenantId}`, [storageKey, tenantId]);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [you, setYou] = useState();
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [open, setOpenState] = useState(false);
  const [unread, setUnread] = useState(0);
  const [lastError, setLastError] = useState();
  const [uploading, setUploading] = useState(false);
  const [lastUploadError, setLastUploadError] = useState();
  const wsRef = useRef(null);
  const reconnectTimer = useRef();
  const reconnectAttempt = useRef(0);
  const identityRef = useRef(null);
  const youRef = useRef();
  const connectRef = useRef();
  const openRef = useRef(open);
  const isMounted = useRef(true);
  const skipJwtRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const idleDisconnectedRef = useRef(false);
  const cleanupSocket = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = void 0;
    }
    if (wsRef.current) {
      try {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
      } catch (_) {
      }
      wsRef.current = null;
    }
  }, []);
  const setOpen = useCallback(
    (next) => {
      var _a;
      setOpenState(next);
      openRef.current = next;
      if (next) {
        setUnread(0);
        idleDisconnectedRef.current = false;
        lastActivityRef.current = Date.now();
        if (connectBehavior === "open") {
          (_a = connectRef.current) == null ? void 0 : _a.call(connectRef);
        }
      } else if (connectBehavior === "open") {
        cleanupSocket();
        setConnected(false);
        setConnecting(false);
      }
    },
    [setOpenState, cleanupSocket, connectBehavior]
  );
  const markActivity = useCallback(() => {
    var _a;
    lastActivityRef.current = Date.now();
    if (idleDisconnectedRef.current) {
      idleDisconnectedRef.current = false;
      if (connectBehavior !== "open" || openRef.current) {
        (_a = connectRef.current) == null ? void 0 : _a.call(connectRef);
      }
    }
  }, [connectBehavior]);
  const persistGuestIdentity = useCallback(
    (identity) => {
      if (!SUPPORTS_STORAGE) return;
      try {
        if (!identity) {
          window.localStorage.removeItem(guestCacheKey);
        } else {
          window.localStorage.setItem(guestCacheKey, JSON.stringify(identity));
        }
      } catch (error) {
        console.warn("Unable to persist chat identity", error);
      }
    },
    [guestCacheKey]
  );
  const readGuestIdentity = useCallback(() => {
    if (!SUPPORTS_STORAGE) return void 0;
    try {
      const value = window.localStorage.getItem(guestCacheKey);
      if (!value) return void 0;
      const parsed = JSON.parse(value);
      if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
        window.localStorage.removeItem(guestCacheKey);
        return void 0;
      }
      return parsed;
    } catch (error) {
      console.warn("Unable to read chat identity", error);
      return void 0;
    }
  }, [guestCacheKey]);
  const userRoomKeyFor = useCallback(
    (userId) => `${userCachePrefix}:${userId}`,
    [userCachePrefix]
  );
  const readUserRoomId = useCallback(
    (userId) => {
      if (!SUPPORTS_STORAGE || !userId) return void 0;
      try {
        return window.localStorage.getItem(userRoomKeyFor(userId)) || void 0;
      } catch (error) {
        console.warn("Unable to read user chat room", error);
        return void 0;
      }
    },
    [userRoomKeyFor]
  );
  const persistUserRoomId = useCallback(
    (userId, value) => {
      if (!SUPPORTS_STORAGE || !userId || !value) return;
      try {
        window.localStorage.setItem(userRoomKeyFor(userId), value);
      } catch (error) {
        console.warn("Unable to persist user chat room", error);
      }
    },
    [userRoomKeyFor]
  );
  const buildUrl = useCallback(
    (path) => {
      const prefix = baseEndpoint || endpoint;
      return `${prefix}${path}`;
    },
    [baseEndpoint, endpoint]
  );
  const requestGuestToken = useCallback(async () => {
    var _a, _b;
    const cached = readGuestIdentity();
    if (cached) {
      if (!roomId || cached.roomId === roomId) {
        log("token:cached", { tenantId, roomId: cached.roomId });
        return cached;
      }
      log("token:discarded:mismatch", { tenantId, cachedRoom: cached.roomId, requestedRoom: roomId });
      persistGuestIdentity(null);
    }
    const headers = {
      "content-type": "application/json",
      ...(_a = fetchOptions == null ? void 0 : fetchOptions.headers) != null ? _a : {}
    };
    const payload = {
      tenantId,
      roomId,
      name: displayName
    };
    if (role) {
      payload.role = role;
    }
    const response = await fetch(buildUrl("/auth/guest"), {
      ...fetchOptions,
      method: "POST",
      headers,
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      throw new Error("Guest auth failed");
    }
    const body = await response.json();
    const record = {
      token: body.token,
      guestId: body.guestId,
      roomId: roomId != null ? roomId : body.roomId,
      displayName: body.displayName,
      expiresAt: Date.now() + ((_b = body.expiresIn) != null ? _b : 55 * 60 * 1e3)
    };
    persistGuestIdentity(record);
    log("token:issued", { tenantId, roomId: record.roomId, role });
    return record;
  }, [buildUrl, readGuestIdentity, fetchOptions, tenantId, roomId, displayName, persistGuestIdentity, role]);
  const decodeJwt = (token) => {
    if (!token) return void 0;
    try {
      const [, payload] = token.split(".");
      if (!payload) return void 0;
      const data = JSON.parse(atob(payload));
      return data;
    } catch (error) {
      console.warn("Unable to decode JWT", error);
      return void 0;
    }
  };
  const resolveUserKey = (decoded) => {
    var _a, _b, _c;
    return (decoded == null ? void 0 : decoded.roomId) || (decoded == null ? void 0 : decoded.sub) || (decoded == null ? void 0 : decoded.userId) || (decoded == null ? void 0 : decoded.id) || (decoded == null ? void 0 : decoded._id) || (decoded == null ? void 0 : decoded.email) || (decoded == null ? void 0 : decoded.username) || ((_a = decoded == null ? void 0 : decoded.user) == null ? void 0 : _a.id) || ((_b = decoded == null ? void 0 : decoded.user) == null ? void 0 : _b._id) || ((_c = decoded == null ? void 0 : decoded.user) == null ? void 0 : _c.email);
  };
  const deriveUserRoomId = (decoded) => {
    var _a;
    if (roomId) return roomId;
    if (decoded == null ? void 0 : decoded.roomId) return decoded.roomId;
    if (decoded == null ? void 0 : decoded.sub) return decoded.sub;
    if (((_a = identityRef.current) == null ? void 0 : _a.kind) === "jwt" && identityRef.current.roomId) {
      return identityRef.current.roomId;
    }
    return void 0;
  };
  const ensureIdentity = useCallback(async () => {
    const strategy = authStrategy;
    const cachedGuest = readGuestIdentity();
    const viaJwt = async () => {
      var _a, _b, _c, _d;
      if (!getJwt) {
        throw new Error("Missing getJwt handler");
      }
      const token = (_a = await getJwt()) == null ? void 0 : _a.trim();
      if (!token) {
        throw new Error("JWT not available");
      }
      const decoded = decodeJwt(token);
      const userKey = resolveUserKey(decoded);
      const cachedRoomId = readUserRoomId(userKey);
      const derivedRoomId = (_d = (_c = (_b = roomId != null ? roomId : cachedRoomId) != null ? _b : deriveUserRoomId(decoded)) != null ? _c : userKey) != null ? _d : crypto.randomUUID();
      persistUserRoomId(userKey != null ? userKey : derivedRoomId, derivedRoomId);
      return {
        token,
        roomId: derivedRoomId,
        displayName: displayName != null ? displayName : decoded == null ? void 0 : decoded.name,
        kind: "jwt"
      };
    };
    const viaGuest = async () => {
      var _a;
      const guest = await requestGuestToken();
      return {
        token: guest.token,
        roomId: guest.roomId,
        displayName: (_a = guest.displayName) != null ? _a : displayName,
        kind: "guest"
      };
    };
    if (strategy === "jwt") {
      return viaJwt();
    }
    if (strategy === "guest") {
      return viaGuest();
    }
    try {
      return await viaJwt();
    } catch (error) {
      console.info("Falling back to guest token", error);
      return viaGuest();
    }
  }, [authStrategy, getJwt, roomId, displayName, requestGuestToken, readGuestIdentity, readUserRoomId, persistUserRoomId]);
  const buildWebSocketUrl = useCallback(
    (activeRoom) => {
      const url = new URL(baseEndpoint || endpoint);
      url.protocol = url.protocol === "http:" ? "ws:" : "wss:";
      url.pathname = `${url.pathname.replace(/\/$/, "")}/ws/${encodeURIComponent(tenantId)}/${encodeURIComponent(activeRoom)}`;
      url.search = "";
      url.hash = "";
      return url.toString();
    },
    [baseEndpoint, endpoint, tenantId]
  );
  const handleServerMessage = useCallback(
    (raw) => {
      var _a, _b, _c, _d, _e;
      let data;
      try {
        data = JSON.parse(raw);
      } catch (error) {
        console.warn("Invalid chat payload", error);
        return;
      }
      switch (data.t) {
        case "history": {
          const ordered = dedupeMessages((_a = data.messages) != null ? _a : []);
          log("ws:history", { count: ordered.length, roomKey: (_b = youRef.current) == null ? void 0 : _b.id });
          setMessages(ordered);
          break;
        }
        case "joined": {
          youRef.current = data.you;
          log("ws:joined", { you: data.you, peers: data.peers.length });
          setYou(data.you);
          setParticipants([data.you, ...data.peers]);
          break;
        }
        case "msg": {
          const message = data.message;
          log("ws:message", {
            id: message.id,
            from: message.from.role,
            type: message.type,
            textLength: (message.text || "").length,
            textPreview: message.type === "file" ? `file:${((_c = message.file) == null ? void 0 : _c.name) || ((_d = message.file) == null ? void 0 : _d.url) || "attachment"}` : previewText(message.text)
          });
          setMessages((prev) => {
            if (prev.find((m) => m.id === message.id)) return prev;
            const next = [...prev, message].slice(-200);
            return next;
          });
          if (!openRef.current && message.from.id !== ((_e = youRef.current) == null ? void 0 : _e.id)) {
            setUnread((prev) => prev + 1);
          }
          break;
        }
        case "presence": {
          const users = data.users;
          log("ws:presence", { count: users.length });
          setParticipants((prev) => mergeParticipants(prev, users));
          break;
        }
        case "typing": {
          const typingId = data.id;
          const typingOn = data.on;
          log("ws:typing", { id: typingId, on: typingOn });
          setParticipants(
            (prev) => prev.map(
              (user) => user.id === typingId ? {
                ...user,
                typing: typingOn
              } : user
            )
          );
          break;
        }
        case "system": {
          log("ws:system", { text: data.text });
          break;
        }
        default:
          break;
      }
    },
    []
  );
  const scheduleReconnect = useCallback(
    (immediate = false) => {
      if (disabled) return;
      if (!isMounted.current) return;
      if (connectBehavior === "open" && !openRef.current) return;
      if (idleDisconnectedRef.current) return;
      const attempt = reconnectAttempt.current;
      const delay = immediate ? 2e3 : Math.min(15e3, 1e3 * 2 ** attempt);
      reconnectAttempt.current = attempt + 1;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      reconnectTimer.current = setTimeout(() => {
        var _a;
        (_a = connectRef.current) == null ? void 0 : _a.call(connectRef);
      }, delay);
    },
    [disabled, connectBehavior]
  );
  const connect = useCallback(async () => {
    if (disabled) {
      cleanupSocket();
      return;
    }
    if (!endpoint) {
      setLastError("Chat endpoint missing");
      return;
    }
    if (connectBehavior === "open" && !openRef.current) {
      cleanupSocket();
      setConnected(false);
      setConnecting(false);
      return;
    }
    cleanupSocket();
    setConnecting(true);
    setLastError(void 0);
    try {
      const identity = await ensureIdentity();
      identityRef.current = identity;
      const activeRoomKey = identity.roomId;
      if (!activeRoomKey) {
        throw new Error("Missing room identifier");
      }
      const wsUrl = buildWebSocketUrl(activeRoomKey);
      log("ws:connect:start", { roomId: activeRoomKey, url: wsUrl });
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      reconnectAttempt.current = 0;
      ws.onopen = () => {
        var _a;
        setConnecting(false);
        setConnected(true);
        markActivity();
        log("ws:connect:open", { roomId: activeRoomKey });
        ws.send(
          JSON.stringify({
            t: "join",
            token: identity.token,
            name: (_a = identity.displayName) != null ? _a : displayName
          })
        );
      };
      ws.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        markActivity();
        handleServerMessage(event.data);
      };
      ws.onerror = (event) => {
        setLastError("WebSocket error");
        log("ws:error", { roomId: activeRoomKey, event });
      };
      ws.onclose = (event) => {
        setConnected(false);
        setConnecting(false);
        if (((event == null ? void 0 : event.code) === 4401 || (event == null ? void 0 : event.code) === 4403) && authStrategy === "auto") {
          skipJwtRef.current = true;
        }
        log("ws:close", { code: event == null ? void 0 : event.code, reason: event == null ? void 0 : event.reason, roomId: activeRoomKey });
        scheduleReconnect();
      };
    } catch (error) {
      setConnecting(false);
      setConnected(false);
      const message = error instanceof Error ? error.message : "Unable to connect";
      setLastError(message);
      log("ws:connect:error", { error: message });
      scheduleReconnect(true);
    }
  }, [disabled, endpoint, cleanupSocket, ensureIdentity, buildWebSocketUrl, displayName, handleServerMessage, scheduleReconnect, authStrategy, connectBehavior, markActivity]);
  connectRef.current = connect;
  const sendMessage = useCallback(
    (text) => {
      var _a;
      const trimmed = text.trim();
      if (!trimmed || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return;
      }
      log("ws:send", {
        roomId: (_a = identityRef.current) == null ? void 0 : _a.roomId,
        textLength: trimmed.length,
        textPreview: previewText(trimmed)
      });
      markActivity();
      wsRef.current.send(JSON.stringify({ t: "say", text: trimmed }));
    },
    [markActivity]
  );
  const sendFile = useCallback(
    async (file) => {
      var _a, _b, _c, _d, _e;
      if (!file) return;
      if (!uploadFile) {
        setLastUploadError("File uploads are not configured.");
        return;
      }
      if (maxUploadBytes > 0 && file.size > maxUploadBytes) {
        const maxMb = Math.max(1, Math.round(maxUploadBytes / (1024 * 1024)));
        setLastUploadError(`File is too large. Maximum size is ${maxMb} MB.`);
        return;
      }
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setLastUploadError("Chat is offline. Reconnect before uploading files.");
        return;
      }
      setUploading(true);
      setLastUploadError(void 0);
      try {
        const resolvedRoomId = ((_a = identityRef.current) == null ? void 0 : _a.roomId) || roomId;
        const uploaded = await uploadFile({
          file,
          tenantId,
          roomId: resolvedRoomId,
          authToken: (_b = identityRef.current) == null ? void 0 : _b.token,
          you: youRef.current
        });
        const url = (_c = uploaded == null ? void 0 : uploaded.url) == null ? void 0 : _c.trim();
        if (!url) {
          throw new Error("Upload succeeded but no file URL was returned.");
        }
        const attachment = {
          url,
          name: ((_d = uploaded.name) == null ? void 0 : _d.trim()) || file.name,
          type: uploaded.type || file.type || void 0,
          size: typeof uploaded.size === "number" ? uploaded.size : file.size,
          key: ((_e = uploaded.key) == null ? void 0 : _e.trim()) || void 0
        };
        const socket = wsRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          throw new Error("Connection dropped before file could be sent.");
        }
        markActivity();
        socket.send(
          JSON.stringify({
            t: "say",
            file: attachment
          })
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to upload file.";
        setLastUploadError(message);
        log("ws:file-upload:error", { message, fileName: file.name, size: file.size });
      } finally {
        setUploading(false);
      }
    },
    [uploadFile, maxUploadBytes, markActivity, roomId, tenantId]
  );
  const sendTyping = useCallback((on) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    markActivity();
    wsRef.current.send(JSON.stringify({ t: "typing", on }));
  }, [markActivity]);
  useEffect(() => {
    log("provider:mount", { tenantId, roomId, role, strategy: authStrategy });
    return () => {
      log("provider:unmount", { tenantId, roomId });
    };
  }, [tenantId, roomId, role, authStrategy]);
  useEffect(() => {
    if (disabled) {
      cleanupSocket();
      setConnected(false);
      setConnecting(false);
      return;
    }
    if (connectBehavior === "open" && !open) {
      cleanupSocket();
      setConnected(false);
      setConnecting(false);
      return;
    }
    connect();
    return () => {
      cleanupSocket();
    };
  }, [connect, cleanupSocket, disabled, connectBehavior, open]);
  useEffect(() => {
    if (!idleDisconnectMs || idleDisconnectMs <= 0) return;
    if (disabled) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const onActivity = () => {
      markActivity();
    };
    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        idleDisconnectedRef.current = true;
        cleanupSocket();
        setConnected(false);
        setConnecting(false);
      } else {
        markActivity();
      }
    };
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("mousedown", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);
    window.addEventListener("touchstart", onActivity, { passive: true });
    window.addEventListener("scroll", onActivity, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setInterval(() => {
      if (connectBehavior === "open" && !openRef.current) return;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor < idleDisconnectMs) return;
      idleDisconnectedRef.current = true;
      cleanupSocket();
      setConnected(false);
      setConnecting(false);
      setLastError("Disconnected due to inactivity");
    }, Math.min(1e4, Math.max(1e3, Math.floor(idleDisconnectMs / 2))));
    return () => {
      clearInterval(timer);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("mousedown", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("touchstart", onActivity);
      window.removeEventListener("scroll", onActivity);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [idleDisconnectMs, disabled, connectBehavior, cleanupSocket, markActivity]);
  useEffect(() => {
    return () => {
      isMounted.current = false;
      cleanupSocket();
    };
  }, [cleanupSocket]);
  useEffect(() => {
    skipJwtRef.current = false;
  }, [authStrategy, tenantId, roomId]);
  useEffect(() => {
    openRef.current = open;
    if (open) {
      setUnread(0);
    }
  }, [open]);
  const contextValue = useMemo(
    () => ({
      connected,
      connecting,
      open,
      setOpen,
      messages,
      participants,
      you,
      unread,
      sendMessage,
      sendFile,
      sendTyping,
      canUpload: Boolean(uploadFile),
      uploading,
      uploadAccept,
      lastUploadError,
      reconnect: () => {
        reconnectAttempt.current = 0;
        connect();
      },
      lastError
    }),
    [
      connected,
      connecting,
      open,
      setOpen,
      messages,
      participants,
      you,
      unread,
      sendMessage,
      sendFile,
      sendTyping,
      uploadFile,
      uploading,
      uploadAccept,
      lastUploadError,
      connect,
      lastError
    ]
  );
  return /* @__PURE__ */ jsx(ChatContext.Provider, { value: contextValue, children });
};
function dedupeMessages(messages) {
  const map = /* @__PURE__ */ new Map();
  messages.forEach((msg) => {
    map.set(msg.id, msg);
  });
  return Array.from(map.values()).sort((a, b) => a.at - b.at);
}
function mergeParticipants(prev, incoming) {
  const merged = /* @__PURE__ */ new Map();
  prev.forEach((p) => merged.set(p.id, p));
  incoming.forEach((p) => merged.set(p.id, { ...merged.get(p.id), ...p }));
  return Array.from(merged.values());
}

// src/components/FloatingChat.tsx
import { useEffect as useEffect3, useMemo as useMemo3, useRef as useRef3, useState as useState3 } from "react";
import {
  Badge,
  Box as Box3,
  Drawer,
  Fab,
  IconButton as IconButton3,
  Stack as Stack3,
  TextField,
  Typography as Typography3,
  useTheme,
  Chip,
  Divider,
  Tooltip,
  Button as Button3
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon2 from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// src/hooks/useChat.ts
var useChat = () => useChatContext();

// src/components/ImagePreviewDialog.tsx
import { useEffect as useEffect2, useMemo as useMemo2, useRef as useRef2, useState as useState2 } from "react";
import { Box, Button, Dialog, IconButton, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var MIN_SCALE = 1;
var MAX_SCALE = 6;
var SCALE_STEP = 0.2;
var clampScale = (value) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(value.toFixed(2))));
var getDistance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
var ImagePreviewDialog = ({
  open,
  imageUrl,
  imageName,
  onClose,
  zIndex
}) => {
  const [scale, setScale] = useState2(1);
  const [offset, setOffset] = useState2({ x: 0, y: 0 });
  const [dragging, setDragging] = useState2(false);
  const dragStart = useRef2({ x: 0, y: 0 });
  const pointers = useRef2(/* @__PURE__ */ new Map());
  const pinchStart = useRef2(null);
  useEffect2(() => {
    if (open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setDragging(false);
      pointers.current.clear();
      pinchStart.current = null;
    }
  }, [open, imageUrl]);
  const canPan = scale > 1;
  const handleZoomBy = (delta) => {
    setScale((prev) => {
      const next = clampScale(prev + delta);
      if (next === MIN_SCALE) {
        setOffset({ x: 0, y: 0 });
      }
      return next;
    });
  };
  const onWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
    handleZoomBy(delta);
  };
  const onPointerDown = (event) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);
    if (pointers.current.size === 2) {
      const [first, second] = Array.from(pointers.current.values());
      pinchStart.current = { distance: getDistance(first, second), scale };
      setDragging(false);
      return;
    }
    if (pointers.current.size === 1 && canPan) {
      setDragging(true);
      dragStart.current = {
        x: event.clientX - offset.x,
        y: event.clientY - offset.y
      };
    }
  };
  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const [first, second] = Array.from(pointers.current.values());
      const distance = getDistance(first, second);
      if (pinchStart.current.distance > 0) {
        const next = clampScale(pinchStart.current.scale * (distance / pinchStart.current.distance));
        setScale(next);
        if (next === MIN_SCALE) {
          setOffset({ x: 0, y: 0 });
        }
      }
      return;
    }
    if (!dragging || !canPan) return;
    setOffset({
      x: event.clientX - dragStart.current.x,
      y: event.clientY - dragStart.current.y
    });
  };
  const endDrag = (event) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) {
      pinchStart.current = null;
    }
    if (!pointers.current.size) {
      setDragging(false);
      return;
    }
    const [remaining] = Array.from(pointers.current.values());
    if (scale > 1 && remaining) {
      setDragging(true);
      dragStart.current = {
        x: remaining.x - offset.x,
        y: remaining.y - offset.y
      };
    } else {
      setDragging(false);
    }
  };
  const transform = useMemo2(
    () => `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
    [offset.x, offset.y, scale]
  );
  return /* @__PURE__ */ jsx2(
    Dialog,
    {
      fullScreen: true,
      open,
      onClose,
      sx: zIndex ? { zIndex } : void 0,
      PaperProps: { sx: { backgroundColor: "transparent" } },
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          onWheel,
          onPointerMove,
          onPointerUp: endDrag,
          onPointerCancel: endDrag,
          sx: {
            height: "100%",
            width: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.94)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            position: "relative",
            overflow: "hidden",
            touchAction: "none"
          },
          children: [
            /* @__PURE__ */ jsxs(
              Stack,
              {
                direction: "row",
                spacing: 1,
                sx: { position: "absolute", top: 12, right: 12, zIndex: 1 },
                alignItems: "center",
                children: [
                  imageUrl && /* @__PURE__ */ jsx2(
                    Button,
                    {
                      component: "a",
                      href: imageUrl,
                      download: imageName,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      size: "small",
                      variant: "contained",
                      startIcon: /* @__PURE__ */ jsx2(DownloadIcon, {}),
                      children: "Download"
                    }
                  ),
                  /* @__PURE__ */ jsx2(
                    IconButton,
                    {
                      onClick: onClose,
                      "aria-label": "Close image preview",
                      sx: { color: "common.white", backgroundColor: "rgba(255,255,255,0.14)" },
                      children: /* @__PURE__ */ jsx2(CloseIcon, {})
                    }
                  )
                ]
              }
            ),
            imageUrl && /* @__PURE__ */ jsx2(
              Box,
              {
                component: "img",
                src: imageUrl,
                alt: imageName || "Image attachment",
                onPointerDown,
                onDoubleClick: () => {
                  if (scale === 1) {
                    setScale(2);
                  } else {
                    setScale(1);
                    setOffset({ x: 0, y: 0 });
                  }
                },
                sx: {
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transform,
                  transformOrigin: "center center",
                  transition: dragging ? "none" : "transform 120ms ease-out",
                  cursor: canPan ? dragging ? "grabbing" : "grab" : "zoom-in",
                  userSelect: "none"
                },
                draggable: false
              }
            )
          ]
        }
      )
    }
  );
};

// src/components/FileAttachment.tsx
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Box as Box2, Stack as Stack2, Typography } from "@mui/material";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var FileAttachment = ({
  file,
  isImage = false,
  fileMeta,
  onPreviewImage,
  containerSx,
  imageSx,
  linkSx,
  metaSx
}) => {
  if (!(file == null ? void 0 : file.url)) return null;
  return /* @__PURE__ */ jsxs2(Stack2, { spacing: 0.25, sx: containerSx, children: [
    isImage ? /* @__PURE__ */ jsx3(
      Box2,
      {
        component: "img",
        src: file.url,
        alt: file.name || "Image attachment",
        onClick: onPreviewImage ? () => onPreviewImage(file.url, file.name) : void 0,
        sx: {
          maxWidth: 220,
          maxHeight: 220,
          borderRadius: 1,
          cursor: onPreviewImage ? "pointer" : "default",
          objectFit: "cover",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          ...imageSx
        }
      }
    ) : /* @__PURE__ */ jsxs2(
      Typography,
      {
        component: "a",
        href: file.url,
        target: "_blank",
        rel: "noopener noreferrer",
        variant: "body2",
        sx: {
          textDecoration: "underline",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          ...linkSx
        },
        children: [
          /* @__PURE__ */ jsx3(AttachFileIcon, { fontSize: "inherit" }),
          file.name || "Attachment"
        ]
      }
    ),
    fileMeta && /* @__PURE__ */ jsx3(Typography, { variant: "caption", sx: metaSx, children: fileMeta })
  ] });
};

// src/components/UploadButton.tsx
import AttachFileIcon2 from "@mui/icons-material/AttachFile";
import { Button as Button2, IconButton as IconButton2 } from "@mui/material";
import { jsx as jsx4 } from "react/jsx-runtime";
var UploadButton = ({
  onClick,
  visible = true,
  uploading = false,
  disabled = false,
  mode = "icon",
  ariaLabel = "Attach file",
  label = "File",
  sx
}) => {
  if (!visible) return null;
  const isDisabled = disabled || uploading;
  if (mode === "button") {
    return /* @__PURE__ */ jsx4(Button2, { variant: "outlined", onClick, disabled: isDisabled, startIcon: /* @__PURE__ */ jsx4(AttachFileIcon2, {}), sx, children: label });
  }
  return /* @__PURE__ */ jsx4(IconButton2, { onClick, color: "primary", disabled: isDisabled, "aria-label": ariaLabel, sx, children: /* @__PURE__ */ jsx4(AttachFileIcon2, {}) });
};

// src/components/UploadInput.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var UploadInput = ({
  fileInputRef,
  enabled = true,
  accept,
  onSelectFile
}) => {
  if (!enabled) return null;
  return /* @__PURE__ */ jsx5(
    "input",
    {
      ref: fileInputRef,
      type: "file",
      accept,
      style: { display: "none" },
      onChange: async (event) => {
        var _a;
        const file = (_a = event.target.files) == null ? void 0 : _a[0];
        event.currentTarget.value = "";
        if (!file) return;
        await onSelectFile(file);
      }
    }
  );
};

// src/components/UploadError.tsx
import { Typography as Typography2 } from "@mui/material";
import { jsx as jsx6 } from "react/jsx-runtime";
var UploadError = ({ message, sx }) => {
  if (!message) return null;
  return /* @__PURE__ */ jsx6(Typography2, { variant: "caption", color: "error", sx, children: message });
};

// src/components/FloatingChat.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var MAX_DRAFT_CHARS = 4e3;
var MAX_DRAFT_LINES = 200;
var FloatingChat = ({
  projectName = "Support",
  launcherPosition = "right",
  primary,
  disabled,
  hideLauncher = false,
  zIndex = 1500
}) => {
  const {
    open,
    setOpen,
    messages,
    sendMessage,
    sendFile,
    sendTyping,
    connected,
    unread,
    participants,
    lastError,
    lastUploadError,
    canUpload,
    uploading,
    uploadAccept,
    you
  } = useChat();
  const [draft, setDraft] = useState3("");
  const [showScrollHint, setShowScrollHint] = useState3(false);
  const [mounted, setMounted] = useState3(false);
  const [previewImage, setPreviewImage] = useState3(null);
  const messageRef = useRef3(null);
  const fileInputRef = useRef3(null);
  const theme = useTheme();
  useEffect3(() => {
    setMounted(true);
  }, []);
  const embedded = useMemo3(() => {
    var _a;
    if (typeof window === "undefined") return false;
    try {
      if ((_a = window.ReactNativeWebView) == null ? void 0 : _a.postMessage) return true;
    } catch (e) {
    }
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }, []);
  const accent = primary || theme.palette.primary.main;
  const enforceDraftLimit = (nextValue) => {
    let next = nextValue;
    if (next.length > MAX_DRAFT_CHARS) next = next.slice(0, MAX_DRAFT_CHARS);
    const lines = next.split("\n");
    if (lines.length > MAX_DRAFT_LINES) next = lines.slice(0, MAX_DRAFT_LINES).join("\n");
    return next;
  };
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const handleSend = () => {
    if (!draft.trim()) return;
    sendMessage(draft);
    setDraft("");
    sendTyping(false);
  };
  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };
  const handlePickFile = () => {
    var _a;
    if (!canUpload || uploading) return;
    (_a = fileInputRef.current) == null ? void 0 : _a.click();
  };
  const sortedMessages = useMemo3(() => messages.slice().sort((a, b) => a.at - b.at), [messages]);
  useEffect3(() => {
    if (!messageRef.current) return;
    const element = messageRef.current;
    const scrollToBottom = () => {
      element.scrollTop = element.scrollHeight;
    };
    scrollToBottom();
  }, [sortedMessages.length, open]);
  useEffect3(() => {
    if (!messageRef.current) return;
    const element = messageRef.current;
    const onScroll = () => {
      const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
      setShowScrollHint(!nearBottom);
    };
    element.addEventListener("scroll", onScroll);
    return () => element.removeEventListener("scroll", onScroll);
  }, []);
  if (disabled || !mounted || typeof document === "undefined") return null;
  const typingUsers = participants.filter((p) => p.typing && p.id !== (you == null ? void 0 : you.id));
  return /* @__PURE__ */ jsxs3(Fragment, { children: [
    !hideLauncher && /* @__PURE__ */ jsx7(
      Badge,
      {
        color: "error",
        badgeContent: unread,
        overlap: "circular",
        invisible: !unread,
        sx: { position: "fixed", bottom: 24, [launcherPosition]: 24, zIndex },
        children: /* @__PURE__ */ jsx7(Fab, { color: "primary", onClick: toggleDrawer, "aria-label": "Open chat launcher", sx: { backgroundColor: accent }, children: /* @__PURE__ */ jsx7(ChatIcon, {}) })
      }
    ),
    /* @__PURE__ */ jsxs3(
      Drawer,
      {
        anchor: launcherPosition,
        open,
        onClose: () => setOpen(false),
        sx: { zIndex },
        PaperProps: {
          sx: {
            width: 380,
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 0,
            position: "relative",
            minHeight: 0
          }
        },
        ModalProps: {
          keepMounted: true,
          disableScrollLock: true,
          disablePortal: embedded,
          disableEnforceFocus: embedded,
          disableAutoFocus: embedded,
          disableRestoreFocus: embedded,
          container: embedded ? () => {
            if (typeof document === "undefined") return void 0;
            return document.getElementById("___gatsby") || document.body;
          } : void 0
        },
        children: [
          /* @__PURE__ */ jsxs3(Stack3, { direction: "row", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, children: [
            /* @__PURE__ */ jsxs3(Box3, { children: [
              /* @__PURE__ */ jsx7(Typography3, { variant: "subtitle2", color: "text.secondary", children: projectName }),
              /* @__PURE__ */ jsxs3(Stack3, { direction: "row", spacing: 1, alignItems: "center", children: [
                /* @__PURE__ */ jsx7(Chip, { size: "small", color: connected ? "success" : "default", label: connected ? "Online" : "Offline" }),
                lastError && /* @__PURE__ */ jsx7(Tooltip, { title: lastError, children: /* @__PURE__ */ jsx7(Typography3, { variant: "caption", color: "error", children: "Error" }) })
              ] })
            ] }),
            /* @__PURE__ */ jsx7(IconButton3, { onClick: () => setOpen(false), "aria-label": "Close chat", children: /* @__PURE__ */ jsx7(CloseIcon2, {}) })
          ] }),
          /* @__PURE__ */ jsxs3(
            Box3,
            {
              ref: messageRef,
              sx: {
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                px: 2,
                py: 1,
                backgroundColor: theme.palette.background.default
              },
              children: [
                !sortedMessages.length && /* @__PURE__ */ jsx7(Box3, { textAlign: "center", py: 6, color: "text.secondary", children: /* @__PURE__ */ jsx7(Typography3, { variant: "body2", children: "How can we help today?" }) }),
                /* @__PURE__ */ jsx7(Stack3, { spacing: 1.5, sx: { minWidth: 0 }, children: sortedMessages.map((message) => /* @__PURE__ */ jsx7(
                  MessageBubble,
                  {
                    message,
                    accent,
                    selfId: you == null ? void 0 : you.id,
                    selfName: you == null ? void 0 : you.name,
                    onPreviewImage: (url, name) => setPreviewImage({ url, name })
                  },
                  message.id
                )) })
              ]
            }
          ),
          typingUsers.length > 0 && /* @__PURE__ */ jsxs3(Typography3, { variant: "caption", color: "text.secondary", px: 2, pb: 1, children: [
            typingUsers.map((user) => user.name || "Guest").join(", "),
            " typing\u2026"
          ] }),
          /* @__PURE__ */ jsx7(Divider, {}),
          /* @__PURE__ */ jsxs3(Box3, { px: 2, py: 1.5, children: [
            /* @__PURE__ */ jsx7(
              TextField,
              {
                fullWidth: true,
                multiline: true,
                minRows: 1,
                maxRows: 4,
                value: draft,
                onChange: (event) => {
                  const next = enforceDraftLimit(event.target.value);
                  setDraft(next);
                  sendTyping(next.trim().length > 0);
                },
                onKeyDown,
                placeholder: "Type a message",
                helperText: `${draft.length}/${MAX_DRAFT_CHARS}`,
                InputProps: {
                  endAdornment: /* @__PURE__ */ jsxs3(Stack3, { direction: "row", spacing: 0.5, alignItems: "center", children: [
                    /* @__PURE__ */ jsx7(UploadButton, { visible: canUpload, onClick: handlePickFile, uploading, mode: "icon" }),
                    /* @__PURE__ */ jsx7(IconButton3, { onClick: handleSend, color: "primary", disabled: !draft.trim(), "aria-label": "Send message", children: /* @__PURE__ */ jsx7(SendIcon, {}) })
                  ] })
                }
              }
            ),
            /* @__PURE__ */ jsx7(UploadInput, { fileInputRef, enabled: canUpload, accept: uploadAccept, onSelectFile: sendFile }),
            /* @__PURE__ */ jsx7(UploadError, { message: lastUploadError, sx: { display: "block", mt: 0.5 } })
          ] }),
          showScrollHint && /* @__PURE__ */ jsx7(
            Button3,
            {
              variant: "contained",
              size: "small",
              startIcon: /* @__PURE__ */ jsx7(ArrowDownwardIcon, {}),
              sx: { position: "absolute", bottom: 96, right: launcherPosition === "right" ? 24 : void 0, left: launcherPosition === "left" ? 24 : void 0 },
              onClick: () => {
                if (messageRef.current) {
                  messageRef.current.scrollTo({ top: messageRef.current.scrollHeight, behavior: "smooth" });
                }
              },
              children: "New messages"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx7(
      ImagePreviewDialog,
      {
        open: Boolean(previewImage),
        imageUrl: previewImage == null ? void 0 : previewImage.url,
        imageName: previewImage == null ? void 0 : previewImage.name,
        onClose: () => setPreviewImage(null),
        zIndex: zIndex + 1e3
      }
    )
  ] });
};
var normalizeName = (value) => (value || "").trim().toLowerCase();
var IMAGE_EXTENSION_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i;
var formatFileSize = (value) => {
  if (!value || value <= 0) return void 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};
var isImageAttachment = (message) => {
  var _a;
  const file = message.file;
  if (!(file == null ? void 0 : file.url)) return false;
  if ((_a = file.type) == null ? void 0 : _a.toLowerCase().startsWith("image/")) return true;
  const source = file.name || file.url.split("?")[0];
  return IMAGE_EXTENSION_PATTERN.test(source);
};
var MessageBubble = ({ message, accent, selfId, selfName, onPreviewImage }) => {
  var _a;
  const mine = Boolean(selfId && message.from.id === selfId) || Boolean(selfName && message.from.name && normalizeName(selfName) === normalizeName(message.from.name));
  const isSystem = message.type === "system" || message.from.role === "system";
  const alignment = mine ? "flex-end" : "flex-start";
  const label = isSystem ? "System" : mine ? selfName || message.from.name || "You" : message.from.name || (message.from.role === "agent" ? "Support" : "Guest");
  const text = (_a = message.text) != null ? _a : "";
  const file = message.file;
  const isLong = text.length > 1200 || text.split("\n").length > 20;
  const fileMeta = formatFileSize(file == null ? void 0 : file.size);
  const imageAttachment = isImageAttachment(message);
  return /* @__PURE__ */ jsxs3(Stack3, { alignItems: alignment, spacing: 0.5, children: [
    /* @__PURE__ */ jsx7(Typography3, { variant: "caption", color: "text.secondary", children: label }),
    /* @__PURE__ */ jsxs3(
      Box3,
      {
        sx: {
          px: 1.5,
          py: 1,
          maxWidth: "85%",
          borderRadius: 2,
          bgcolor: isSystem ? "action.hover" : mine ? accent : "action.hover",
          color: mine ? "common.white" : "text.primary",
          display: "inline-block",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          minWidth: 0,
          overflowX: "hidden",
          maxHeight: isLong ? "50vh" : void 0,
          overflowY: isLong ? "auto" : "visible"
        },
        children: [
          (file == null ? void 0 : file.url) && /* @__PURE__ */ jsx7(
            FileAttachment,
            {
              file,
              isImage: imageAttachment,
              fileMeta,
              onPreviewImage,
              containerSx: { mb: text ? 0.5 : 0 },
              linkSx: { color: mine ? "inherit" : "primary.main" },
              metaSx: { opacity: 0.8 }
            }
          ),
          text && /* @__PURE__ */ jsx7(Typography3, { variant: "body2", sx: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word" }, children: text })
        ]
      }
    )
  ] });
};

// src/components/CreatedChatsPanel.tsx
import { useCallback as useCallback2, useEffect as useEffect4, useMemo as useMemo4, useRef as useRef4, useState as useState4 } from "react";
import {
  Alert,
  Box as Box4,
  Button as Button4,
  Card,
  CardContent,
  Divider as Divider2,
  List,
  ListItemButton,
  ListItemText,
  Stack as Stack4,
  TextField as TextField2,
  Typography as Typography4
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime.js";
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
dayjs.extend(relativeTime);
var DEFAULT_STORAGE_KEY2 = "sparkle:chat:created";
var CreatedChatsPanel = ({
  endpoint,
  tenantId,
  getJwt,
  currentUserEmail,
  currentUserName,
  title = "Created Chats",
  storageKeyPrefix = DEFAULT_STORAGE_KEY2,
  uploadFile,
  uploadAccept,
  maxUploadBytes
}) => {
  const baseEndpoint = useMemo4(() => endpoint.replace(/\/$/, ""), [endpoint]);
  const [recipientEmail, setRecipientEmail] = useState4("");
  const [subject, setSubject] = useState4("");
  const [lookupEmail, setLookupEmail] = useState4(currentUserEmail || "");
  const [rooms, setRooms] = useState4([]);
  const [selectedRoom, setSelectedRoom] = useState4(null);
  const [error, setError] = useState4();
  const [status, setStatus] = useState4();
  const [loading, setLoading] = useState4(false);
  useEffect4(() => {
    if (currentUserEmail && !lookupEmail) {
      setLookupEmail(currentUserEmail);
    }
  }, [currentUserEmail, lookupEmail]);
  const buildRequestInit = useCallback2(
    async (init) => {
      const token = await getJwt();
      if (!token) throw new Error("Missing auth token");
      const headers = {
        "content-type": "application/json",
        Authorization: `Bearer ${token}`,
        ...init == null ? void 0 : init.headers
      };
      return {
        ...init,
        headers
      };
    },
    [getJwt]
  );
  const handleCreate = async (event) => {
    event.preventDefault();
    setError(void 0);
    setStatus(void 0);
    if (!recipientEmail.trim()) {
      setError("Enter the email you want to chat with.");
      return;
    }
    try {
      const init = await buildRequestInit({
        method: "POST",
        body: JSON.stringify({
          tenantId,
          email: recipientEmail.trim(),
          subject: subject.trim() || void 0
        })
      });
      const response = await fetch(`${baseEndpoint}/rooms/created`, init);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data == null ? void 0 : data.error) || "Unable to create chat");
      }
      const record = await response.json();
      setStatus("Conversation created.");
      setSelectedRoom(record);
      setRecipientEmail("");
      setSubject("");
      setLookupEmail(record.email);
      loadRooms(record.email);
    } catch (err) {
      setError(err.message || "Unable to create chat.");
    }
  };
  const loadRooms = useCallback2(
    async (email) => {
      if (!tenantId || !(email == null ? void 0 : email.trim())) return;
      setLoading(true);
      setError(void 0);
      try {
        const init = await buildRequestInit();
        const response = await fetch(
          `${baseEndpoint}/rooms/created?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(
            email.trim()
          )}`,
          init
        );
        if (!response.ok) throw new Error("Unable to load conversations");
        const payload = await response.json();
        setRooms(payload.rooms || []);
      } catch (err) {
        setError(err.message || "Unable to load conversations.");
      } finally {
        setLoading(false);
      }
    },
    [tenantId, baseEndpoint, buildRequestInit]
  );
  const handleLookup = (event) => {
    event.preventDefault();
    if (!lookupEmail.trim()) {
      setError("Enter an email to look up conversations.");
      return;
    }
    loadRooms(lookupEmail.trim());
  };
  const storageKeyForRoom = (room) => {
    if (!room) return `${storageKeyPrefix}:${tenantId}`;
    return `${storageKeyPrefix}:${tenantId}:${room.roomId}`;
  };
  return /* @__PURE__ */ jsxs4(Stack4, { spacing: 3, children: [
    /* @__PURE__ */ jsx8(Card, { children: /* @__PURE__ */ jsxs4(CardContent, { children: [
      /* @__PURE__ */ jsx8(Typography4, { variant: "h5", mb: 2, children: title }),
      error && /* @__PURE__ */ jsx8(Alert, { severity: "error", sx: { mb: 2 }, children: error }),
      status && /* @__PURE__ */ jsx8(Alert, { severity: "success", sx: { mb: 2 }, children: status }),
      /* @__PURE__ */ jsxs4(Stack4, { component: "form", spacing: 2, onSubmit: handleCreate, children: [
        /* @__PURE__ */ jsx8(Typography4, { variant: "subtitle1", children: "Start a conversation" }),
        /* @__PURE__ */ jsx8(
          TextField2,
          {
            label: "Recipient email",
            value: recipientEmail,
            onChange: (event) => setRecipientEmail(event.target.value),
            type: "email",
            required: true
          }
        ),
        /* @__PURE__ */ jsx8(
          TextField2,
          {
            label: "Subject",
            value: subject,
            onChange: (event) => setSubject(event.target.value),
            placeholder: "Optional context"
          }
        ),
        /* @__PURE__ */ jsx8(Button4, { type: "submit", variant: "contained", children: "Create chat" })
      ] }),
      /* @__PURE__ */ jsx8(Divider2, { sx: { my: 3 } }),
      /* @__PURE__ */ jsxs4(Stack4, { component: "form", spacing: 2, onSubmit: handleLookup, children: [
        /* @__PURE__ */ jsx8(Typography4, { variant: "subtitle1", children: "Find your conversations" }),
        /* @__PURE__ */ jsx8(
          TextField2,
          {
            label: "Lookup email",
            value: lookupEmail,
            onChange: (event) => setLookupEmail(event.target.value),
            placeholder: "Enter the email used when creating the chat"
          }
        ),
        /* @__PURE__ */ jsx8(Button4, { type: "submit", variant: "outlined", disabled: !lookupEmail.trim(), children: "Search" })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx8(Card, { children: /* @__PURE__ */ jsxs4(CardContent, { children: [
      /* @__PURE__ */ jsx8(Typography4, { variant: "h6", mb: 2, children: "Conversations" }),
      !rooms.length && !loading && /* @__PURE__ */ jsx8(Typography4, { variant: "body2", color: "text.secondary", children: "No conversations yet. Create one to get started." }),
      /* @__PURE__ */ jsx8(List, { dense: true, children: rooms.map((room) => /* @__PURE__ */ jsx8(
        ListItemButton,
        {
          selected: (selectedRoom == null ? void 0 : selectedRoom.roomKey) === room.roomKey,
          onClick: () => setSelectedRoom(room),
          children: /* @__PURE__ */ jsx8(
            ListItemText,
            {
              primary: /* @__PURE__ */ jsxs4(Stack4, { direction: "row", justifyContent: "space-between", children: [
                /* @__PURE__ */ jsx8(Typography4, { variant: "subtitle2", children: room.subject || room.email }),
                /* @__PURE__ */ jsx8(Typography4, { variant: "caption", color: "text.secondary", children: dayjs(room.createdAt).fromNow() })
              ] }),
              secondary: /* @__PURE__ */ jsx8(Typography4, { variant: "caption", color: "text.secondary", children: room.email })
            }
          )
        },
        room.roomKey
      )) })
    ] }) }),
    selectedRoom && /* @__PURE__ */ jsx8(Card, { children: /* @__PURE__ */ jsxs4(CardContent, { children: [
      /* @__PURE__ */ jsxs4(Typography4, { variant: "h6", mb: 2, children: [
        "Chat with ",
        selectedRoom.contextLabel || selectedRoom.subject || selectedRoom.email
      ] }),
      /* @__PURE__ */ jsx8(
        ChatProvider,
        {
          tenantId,
          endpoint,
          roomId: selectedRoom.roomId,
          authStrategy: "auto",
          getJwt,
          displayName: currentUserName,
          storageKey: `${storageKeyForRoom(selectedRoom)}`,
          uploadFile,
          uploadAccept,
          maxUploadBytes,
          children: /* @__PURE__ */ jsx8(CreatedChatRoom, {})
        }
      )
    ] }) })
  ] });
};
var CreatedChatRoom = () => {
  const { messages, sendMessage, sendFile, sendTyping, connected, canUpload, uploading, uploadAccept, lastUploadError } = useChat();
  const [draft, setDraft] = useState4("");
  const [previewImage, setPreviewImage] = useState4(null);
  const listRef = useRef4(null);
  const fileInputRef = useRef4(null);
  const IMAGE_EXTENSION_PATTERN3 = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i;
  useEffect4(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);
  const handleSend = () => {
    if (!draft.trim()) return;
    sendMessage(draft.trim());
    setDraft("");
    sendTyping(false);
  };
  const formatFileSize3 = (value) => {
    if (!value || value <= 0) return void 0;
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };
  const isImageAttachment3 = (message) => {
    var _a;
    const file = message.file;
    if (!(file == null ? void 0 : file.url)) return false;
    if ((_a = file.type) == null ? void 0 : _a.toLowerCase().startsWith("image/")) return true;
    const source = file.name || file.url.split("?")[0];
    return IMAGE_EXTENSION_PATTERN3.test(source);
  };
  return /* @__PURE__ */ jsxs4(Stack4, { spacing: 2, children: [
    /* @__PURE__ */ jsxs4(
      Box4,
      {
        ref: listRef,
        sx: {
          minHeight: 240,
          maxHeight: 480,
          overflowY: "auto",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2
        },
        children: [
          !messages.length && /* @__PURE__ */ jsx8(Typography4, { color: "text.secondary", textAlign: "center", children: "No messages yet." }),
          /* @__PURE__ */ jsx8(Stack4, { spacing: 1.5, children: messages.map((message) => {
            var _a;
            return /* @__PURE__ */ jsxs4(Box4, { textAlign: message.from.role === "user" ? "right" : "left", children: [
              /* @__PURE__ */ jsxs4(Typography4, { variant: "caption", color: "text.secondary", children: [
                message.from.name || message.from.role,
                " \xB7 ",
                dayjs(message.at).format("MMM D, h:mm A")
              ] }),
              ((_a = message.file) == null ? void 0 : _a.url) && /* @__PURE__ */ jsx8(
                FileAttachment,
                {
                  file: message.file,
                  isImage: isImageAttachment3(message),
                  fileMeta: formatFileSize3(message.file.size),
                  onPreviewImage: (url, name) => setPreviewImage({ url, name }),
                  imageSx: { display: "block", mt: 0.5 },
                  linkSx: { display: "block" },
                  metaSx: { color: "text.secondary" }
                }
              ),
              message.text && /* @__PURE__ */ jsx8(Typography4, { variant: "body2", sx: { whiteSpace: "pre-wrap" }, children: message.text })
            ] }, message.id);
          }) })
        ]
      }
    ),
    /* @__PURE__ */ jsxs4(Stack4, { direction: "row", spacing: 1, children: [
      /* @__PURE__ */ jsx8(
        TextField2,
        {
          fullWidth: true,
          multiline: true,
          minRows: 1,
          maxRows: 4,
          placeholder: connected ? "Type your message" : "Connecting...",
          value: draft,
          onChange: (event) => {
            setDraft(event.target.value);
            sendTyping(event.target.value.length > 0);
          },
          onKeyDown: (event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }
        }
      ),
      /* @__PURE__ */ jsx8(
        UploadButton,
        {
          visible: canUpload,
          mode: "button",
          onClick: () => {
            var _a;
            return (_a = fileInputRef.current) == null ? void 0 : _a.click();
          },
          uploading
        }
      ),
      /* @__PURE__ */ jsx8(Button4, { variant: "contained", onClick: handleSend, disabled: !draft.trim(), children: "Send" })
    ] }),
    /* @__PURE__ */ jsx8(UploadInput, { fileInputRef, enabled: canUpload, accept: uploadAccept, onSelectFile: sendFile }),
    /* @__PURE__ */ jsx8(UploadError, { message: lastUploadError }),
    /* @__PURE__ */ jsx8(
      ImagePreviewDialog,
      {
        open: Boolean(previewImage),
        imageUrl: previewImage == null ? void 0 : previewImage.url,
        imageName: previewImage == null ? void 0 : previewImage.name,
        onClose: () => setPreviewImage(null)
      }
    )
  ] });
};

// src/components/SupportConsole.tsx
import { useCallback as useCallback3, useEffect as useEffect5, useMemo as useMemo5, useRef as useRef5, useState as useState5 } from "react";
import {
  Alert as Alert2,
  Box as Box5,
  Button as Button5,
  Card as Card2,
  CardContent as CardContent2,
  Chip as Chip2,
  CircularProgress,
  Divider as Divider3,
  IconButton as IconButton4,
  InputAdornment,
  ListItem,
  List as List2,
  ListItemButton as ListItemButton2,
  ListItemText as ListItemText2,
  Menu,
  MenuItem,
  Stack as Stack5,
  Tab,
  Tabs,
  TextField as TextField3,
  ToggleButton,
  ToggleButtonGroup,
  Typography as Typography5
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon2 from "@mui/icons-material/Send";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import dayjs2 from "dayjs";
import relativeTime2 from "dayjs/plugin/relativeTime.js";
import { Fragment as Fragment2, jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
dayjs2.extend(relativeTime2);
var DEFAULT_STORAGE_KEY3 = "sparkle:support:unlock";
var DEFAULT_CHAT_STORAGE_PREFIX = "sparkle:chat:agent";
var DEFAULT_SUPPORT_HEADER = "x-support-console-key";
var ROOMS_POLL_INTERVAL = 1e4;
var DEFAULT_ARCHIVE_STORAGE_PREFIX = "sparkle:support:archived";
var DEFAULT_ROOM_LABEL_STORAGE_PREFIX = "sparkle:support:room-labels";
var MAX_AGENT_MESSAGE_CHARS = 4e3;
var MAX_AGENT_MESSAGE_LINES = 200;
var DEFAULT_ROOMS_VIEW_LIMIT = 100;
var ROOMS_VIEW_PAGE_SIZE = 50;
var readArchivedRooms = (key) => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const record = parsed;
    const normalized = {};
    Object.entries(record).forEach(([roomKey, archivedAt]) => {
      if (typeof archivedAt === "number" && Number.isFinite(archivedAt) && archivedAt > 0) {
        normalized[roomKey] = archivedAt;
      }
    });
    return normalized;
  } catch (e) {
    return {};
  }
};
var persistArchivedRooms = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    if (!value || Object.keys(value).length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
  }
};
var readRoomLabels = (key) => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const record = parsed;
    const normalized = {};
    Object.entries(record).forEach(([roomKey, label]) => {
      if (typeof label === "string" && label.trim().length > 0) {
        normalized[roomKey] = label.trim();
      }
    });
    return normalized;
  } catch (e) {
    return {};
  }
};
var persistRoomLabels = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    if (!value || Object.keys(value).length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
  }
};
var normalizeLabel = (value) => {
  const trimmed = (value != null ? value : "").trim();
  return trimmed ? trimmed : void 0;
};
var isGenericLabel = (value) => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === "agent" || normalized === "support" || normalized === "guest" || normalized === "user";
};
var resolvePeerLabelFromRoomSummary = (room) => {
  const participants = room.participants || [];
  const nonAgents = participants.filter((participant) => participant.role !== "agent");
  const primary = nonAgents.find((participant) => participant.role === "user" && normalizeLabel(participant.name) && !isGenericLabel(participant.name)) || nonAgents.find((participant) => participant.role === "user" && normalizeLabel(participant.id) && !isGenericLabel(participant.id)) || nonAgents.find((participant) => normalizeLabel(participant.name) && !isGenericLabel(participant.name)) || nonAgents.find((participant) => normalizeLabel(participant.id) && !isGenericLabel(participant.id)) || nonAgents[0];
  return normalizeLabel(primary == null ? void 0 : primary.name) || normalizeLabel(primary == null ? void 0 : primary.id);
};
var resolveRoomSubtitle = (room) => {
  const ref = room.lastMessageAt || room.lastActive;
  if (!ref) return "No activity yet";
  return `Updated ${dayjs2(ref).fromNow()}`;
};
var resolveCreatedPrimaryLabel = (room) => {
  if (room.createdContextLabel) return room.createdContextLabel;
  if (room.createdSubject) return room.createdSubject;
  if (room.createdEmail) return room.createdEmail;
  return "Created conversation";
};
var resolveCreatedSecondaryLabel = (room) => {
  var _a;
  const segments = [];
  if (room.createdContextType && room.createdContextId) {
    segments.push(`${room.createdContextType} \xB7 ${room.createdContextId}`);
  }
  if ((_a = room.createdParticipants) == null ? void 0 : _a.length) {
    const emails = room.createdParticipants.map((participant) => participant.email || participant.name).filter(Boolean).join(", ");
    if (emails) {
      segments.push(emails);
    }
  } else if (room.createdEmail) {
    segments.push(room.createdEmail);
  }
  if (room.createdBy && !segments.some((segment) => segment.startsWith("Started by "))) {
    segments.push(`Started by ${room.createdBy}`);
  }
  return segments.join(" \u2022 ") || void 0;
};
var truncateSingleLine = (value, max = 160) => {
  const normalized = (value != null ? value : "").replace(/\s+/g, " ").trim();
  if (!normalized) return void 0;
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, Math.max(0, max - 1))}\u2026`;
};
var IMAGE_EXTENSION_PATTERN2 = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i;
var formatFileSize2 = (value) => {
  if (!value || value <= 0) return void 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};
var isImageAttachment2 = (message) => {
  var _a;
  const file = message.file;
  if (!(file == null ? void 0 : file.url)) return false;
  if ((_a = file.type) == null ? void 0 : _a.toLowerCase().startsWith("image/")) return true;
  const source = file.name || file.url.split("?")[0];
  return IMAGE_EXTENSION_PATTERN2.test(source);
};
var normalizeHeaders = (input) => {
  if (!input) return {};
  if (Array.isArray(input)) {
    return input.reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }
  if (input instanceof Headers) {
    const acc = {};
    input.forEach((value, key) => {
      acc[key] = value;
    });
    return acc;
  }
  return { ...input };
};
var readStoredSecret = (key) => {
  if (typeof window === "undefined") return void 0;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return void 0;
    const parsed = JSON.parse(raw);
    if ((parsed == null ? void 0 : parsed.allowed) && typeof parsed.secret === "string") {
      return parsed.secret;
    }
  } catch (e) {
  }
  return void 0;
};
var persistSecret = (key, secret) => {
  if (typeof window === "undefined") return;
  try {
    if (!secret) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(
        key,
        JSON.stringify({
          allowed: true,
          secret
        })
      );
    }
  } catch (e) {
  }
};
var isGuestRoom = (room) => room.audienceType === "guest";
var isAccountRoom = (room) => room.audienceType === "user" || room.audienceType === "mixed";
var isCreatedRoom = (room) => room.roomType === "created";
var SupportConsole = ({
  endpoint,
  tenantId,
  gatePassword,
  sessionStorageKey = DEFAULT_STORAGE_KEY3,
  storageKeyPrefix = DEFAULT_CHAT_STORAGE_PREFIX,
  pollIntervalMs = ROOMS_POLL_INTERVAL,
  supportHeader = DEFAULT_SUPPORT_HEADER,
  defaultAgentName = "Agent",
  resolveAgentIdentity,
  uploadFile,
  uploadAccept,
  maxUploadBytes,
  unlockMessage
}) => {
  const initialSecret = readStoredSecret(sessionStorageKey);
  const [authorized, setAuthorized] = useState5(Boolean(initialSecret));
  const [consoleSecret, setConsoleSecret] = useState5(initialSecret);
  const [passwordAttempt, setPasswordAttempt] = useState5("");
  const [passwordError, setPasswordError] = useState5();
  const [rooms, setRooms] = useState5([]);
  const [selectedRoom, setSelectedRoom] = useState5(null);
  const [loading, setLoading] = useState5(false);
  const [error, setError] = useState5();
  const [tab, setTab] = useState5("active");
  const [audienceFilter, setAudienceFilter] = useState5("all");
  const [archiveView, setArchiveView] = useState5(false);
  const [searchQuery, setSearchQuery] = useState5("");
  const [roomsViewLimit, setRoomsViewLimit] = useState5(DEFAULT_ROOMS_VIEW_LIMIT);
  const [roomMenuAnchorEl, setRoomMenuAnchorEl] = useState5(null);
  const [roomMenuRoom, setRoomMenuRoom] = useState5(null);
  const archiveStorageKey = useMemo5(
    () => `${DEFAULT_ARCHIVE_STORAGE_PREFIX}:${tenantId}`,
    [tenantId]
  );
  const [archivedRooms, setArchivedRooms] = useState5(() => readArchivedRooms(archiveStorageKey));
  const roomLabelStorageKey = useMemo5(
    () => `${DEFAULT_ROOM_LABEL_STORAGE_PREFIX}:${tenantId}`,
    [tenantId]
  );
  const [roomLabels, setRoomLabels] = useState5(() => readRoomLabels(roomLabelStorageKey));
  const [agentContext, setAgentContext] = useState5();
  const [noteValue, setNoteValue] = useState5("");
  const [noteBaseValue, setNoteBaseValue] = useState5("");
  const [noteMeta, setNoteMeta] = useState5();
  const [noteLoading, setNoteLoading] = useState5(false);
  const [noteError, setNoteError] = useState5();
  const [noteSaving, setNoteSaving] = useState5(false);
  const baseEndpoint = useMemo5(() => endpoint ? endpoint.replace(/\/$/, "") : "", [endpoint]);
  useEffect5(() => {
    setArchivedRooms(readArchivedRooms(archiveStorageKey));
  }, [archiveStorageKey]);
  useEffect5(() => {
    persistArchivedRooms(archiveStorageKey, archivedRooms);
  }, [archiveStorageKey, archivedRooms]);
  useEffect5(() => {
    setRoomLabels(readRoomLabels(roomLabelStorageKey));
  }, [roomLabelStorageKey]);
  useEffect5(() => {
    persistRoomLabels(roomLabelStorageKey, roomLabels);
  }, [roomLabelStorageKey, roomLabels]);
  useEffect5(() => {
    if (authorized && consoleSecret) {
      persistSecret(sessionStorageKey, consoleSecret);
    } else if (!authorized) {
      persistSecret(sessionStorageKey);
    }
  }, [authorized, consoleSecret, sessionStorageKey]);
  const hydrateAgentContext = useCallback3(async () => {
    if (!resolveAgentIdentity) {
      setAgentContext({ name: defaultAgentName });
      return;
    }
    try {
      const resolved = await resolveAgentIdentity();
      setAgentContext({
        name: (resolved == null ? void 0 : resolved.name) || defaultAgentName,
        fetchOptions: resolved == null ? void 0 : resolved.fetchOptions,
        storageKeySuffix: resolved == null ? void 0 : resolved.storageKeySuffix
      });
    } catch (err) {
      console.warn("SupportConsole: unable to resolve agent identity", err);
      setAgentContext({ name: defaultAgentName });
    }
  }, [defaultAgentName, resolveAgentIdentity]);
  useEffect5(() => {
    if (authorized) {
      hydrateAgentContext();
    }
  }, [authorized, hydrateAgentContext]);
  const buildRequestInit = useCallback3(
    (overrides) => {
      var _a;
      const headers = {
        ...normalizeHeaders((_a = agentContext == null ? void 0 : agentContext.fetchOptions) == null ? void 0 : _a.headers)
      };
      if (consoleSecret) {
        headers[supportHeader] = consoleSecret;
      }
      if (overrides == null ? void 0 : overrides.headers) {
        Object.assign(headers, normalizeHeaders(overrides.headers));
      }
      return {
        ...agentContext == null ? void 0 : agentContext.fetchOptions,
        ...overrides,
        headers
      };
    },
    [agentContext == null ? void 0 : agentContext.fetchOptions, consoleSecret, supportHeader]
  );
  const providerFetchOptions = useMemo5(() => buildRequestInit(), [buildRequestInit]);
  const archivedAtForRoom = useCallback3(
    (room) => {
      var _a;
      return (_a = room.archivedAt) != null ? _a : archivedRooms[room.roomKey];
    },
    [archivedRooms]
  );
  const isRoomArchived = useCallback3((room) => Boolean(archivedAtForRoom(room)), [archivedAtForRoom]);
  const setRoomLabel = useCallback3((roomKey, label) => {
    const nextLabel = normalizeLabel(label);
    if (!nextLabel || isGenericLabel(nextLabel)) return;
    setRoomLabels((prev) => {
      if (prev[roomKey] === nextLabel) return prev;
      return {
        ...prev,
        [roomKey]: nextLabel
      };
    });
  }, []);
  const syncRoomLabels = useCallback3(
    (nextRooms) => {
      setRoomLabels((prev) => {
        let changed = false;
        const next = { ...prev };
        const knownRoomKeys = new Set(nextRooms.map((room) => room.roomKey));
        Object.keys(next).forEach((roomKey) => {
          if (!knownRoomKeys.has(roomKey)) {
            delete next[roomKey];
            changed = true;
          }
        });
        nextRooms.forEach((room) => {
          if (isCreatedRoom(room)) return;
          const peerLabel = resolvePeerLabelFromRoomSummary(room);
          if (!peerLabel || isGenericLabel(peerLabel)) return;
          if (!next[room.roomKey]) {
            next[room.roomKey] = peerLabel;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    },
    [setRoomLabels]
  );
  const resolveRoomDisplayTitle = useCallback3(
    (room) => {
      if (isCreatedRoom(room)) return resolveCreatedPrimaryLabel(room);
      const peerLabel = resolvePeerLabelFromRoomSummary(room);
      if (peerLabel && !isGenericLabel(peerLabel)) return peerLabel;
      const cached = roomLabels[room.roomKey];
      if (cached) return cached;
      if (room.roomId) return room.roomId;
      if (room.roomKey) return room.roomKey;
      return "Conversation";
    },
    [roomLabels]
  );
  const syncArchivedRooms = useCallback3(
    (nextRooms) => {
      setArchivedRooms((prev) => {
        let changed = false;
        const next = { ...prev };
        const knownRoomKeys = new Set(nextRooms.map((room) => room.roomKey));
        Object.keys(next).forEach((roomKey) => {
          if (!knownRoomKeys.has(roomKey)) {
            delete next[roomKey];
            changed = true;
          }
        });
        nextRooms.forEach((room) => {
          var _a, _b;
          const archivedAt = next[room.roomKey];
          if (!archivedAt) return;
          const activityAt = (_b = (_a = room.lastMessageAt) != null ? _a : room.lastActive) != null ? _b : 0;
          if (activityAt && activityAt > archivedAt) {
            delete next[room.roomKey];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    },
    [setArchivedRooms]
  );
  const fetchRooms = useCallback3(async () => {
    if (!baseEndpoint || !tenantId) return;
    setLoading(true);
    setError(void 0);
    try {
      const response = await fetch(`${baseEndpoint}/rooms?tenantId=${encodeURIComponent(tenantId)}`, buildRequestInit());
      if (!response.ok) {
        throw new Error("Unable to load rooms");
      }
      const body = await response.json();
      const nextRooms = body.rooms || [];
      syncArchivedRooms(nextRooms);
      syncRoomLabels(nextRooms);
      setRooms(nextRooms);
    } catch (err) {
      console.error("SupportConsole: unable to load rooms", err);
      setError("Unable to load rooms. Confirm the chat endpoint is reachable.");
    } finally {
      setLoading(false);
    }
  }, [baseEndpoint, tenantId, buildRequestInit, syncArchivedRooms, syncRoomLabels]);
  useEffect5(() => {
    if (!authorized) return;
    fetchRooms();
    const interval = setInterval(fetchRooms, pollIntervalMs);
    return () => clearInterval(interval);
  }, [authorized, fetchRooms, pollIntervalMs]);
  useEffect5(() => {
    if (!selectedRoom) return;
    const updated = rooms.find((room) => room.roomKey === selectedRoom.roomKey);
    if (!updated) {
      setSelectedRoom(null);
      return;
    }
    if (updated !== selectedRoom) {
      setSelectedRoom(updated);
    }
  }, [rooms, selectedRoom]);
  const unlock = (event) => {
    event.preventDefault();
    if (!passwordAttempt.trim()) {
      setPasswordError("Enter the console password.");
      return;
    }
    if (passwordAttempt.trim() !== gatePassword) {
      setPasswordError("Incorrect password. Please try again.");
      return;
    }
    setPasswordError(void 0);
    setAuthorized(true);
    setConsoleSecret(passwordAttempt.trim());
    setPasswordAttempt("");
  };
  const handleTabChange = (_event, value) => {
    setTab(value);
  };
  const handleAudienceFilter = (_event, value) => {
    if (value) setAudienceFilter(value);
  };
  const matchesArchiveView = useCallback3(
    (room) => archiveView ? isRoomArchived(room) : !isRoomArchived(room),
    [archiveView, isRoomArchived]
  );
  const normalizedSearchQuery = useMemo5(() => searchQuery.trim().toLowerCase(), [searchQuery]);
  const matchesSearchQuery = useCallback3(
    (room) => {
      var _a, _b;
      if (!normalizedSearchQuery) return true;
      const tokens = [
        room.roomKey,
        room.roomId,
        room.audienceType,
        room.status,
        room.notePreview,
        room.lastMessagePreview
      ];
      if (isCreatedRoom(room)) {
        tokens.push(resolveCreatedPrimaryLabel(room));
        tokens.push(resolveCreatedSecondaryLabel(room));
        tokens.push(room.createdEmail);
        tokens.push(room.createdSubject);
        tokens.push(room.createdBy);
        (_a = room.createdParticipants) == null ? void 0 : _a.forEach((participant) => {
          tokens.push(participant.email);
          tokens.push(participant.name);
          tokens.push(participant.role);
        });
      } else {
        tokens.push(resolveRoomDisplayTitle(room));
        tokens.push(roomLabels[room.roomKey]);
        (_b = room.participants) == null ? void 0 : _b.forEach((participant) => {
          tokens.push(participant.name);
          tokens.push(participant.id);
          tokens.push(participant.role);
        });
      }
      return tokens.filter((token) => typeof token === "string" && token.trim().length > 0).some((token) => token.toLowerCase().includes(normalizedSearchQuery));
    },
    [normalizedSearchQuery, resolveRoomDisplayTitle, roomLabels]
  );
  const tabCounts = useMemo5(() => {
    const visibleRooms2 = rooms.filter((room) => matchesArchiveView(room));
    return {
      active: visibleRooms2.filter((room) => room.status !== "closed" && !isCreatedRoom(room)).length,
      history: visibleRooms2.filter((room) => {
        var _a;
        return ((_a = room.historySize) != null ? _a : 0) > 0 && !isCreatedRoom(room);
      }).length,
      created: visibleRooms2.filter((room) => isCreatedRoom(room)).length
    };
  }, [rooms, matchesArchiveView]);
  const filteredRooms = useMemo5(() => {
    let baseList = [];
    if (tab === "active") {
      baseList = rooms.filter((room) => matchesArchiveView(room) && room.status !== "closed" && !isCreatedRoom(room));
    } else if (tab === "history") {
      baseList = rooms.filter((room) => {
        var _a;
        return matchesArchiveView(room) && ((_a = room.historySize) != null ? _a : 0) > 0 && !isCreatedRoom(room);
      });
    } else {
      baseList = rooms.filter((room) => matchesArchiveView(room) && isCreatedRoom(room));
    }
    if (tab === "created") {
      return baseList;
    }
    return baseList.filter((room) => {
      if (audienceFilter === "guest") return isGuestRoom(room);
      if (audienceFilter === "account") return isAccountRoom(room);
      return true;
    }).filter(matchesSearchQuery);
  }, [rooms, tab, audienceFilter, matchesArchiveView, matchesSearchQuery]);
  useEffect5(() => {
    setRoomsViewLimit(DEFAULT_ROOMS_VIEW_LIMIT);
  }, [tab, audienceFilter, archiveView, searchQuery]);
  const visibleRooms = useMemo5(
    () => filteredRooms.slice(0, Math.max(0, roomsViewLimit)),
    [filteredRooms, roomsViewLimit]
  );
  const hasMoreRooms = filteredRooms.length > visibleRooms.length;
  const handleOpenRoomMenu = (event, room) => {
    event.stopPropagation();
    setRoomMenuAnchorEl(event.currentTarget);
    setRoomMenuRoom(room);
  };
  const handleCloseRoomMenu = () => {
    setRoomMenuAnchorEl(null);
    setRoomMenuRoom(null);
  };
  const archiveRoom = useCallback3(
    (room) => {
      var _a, _b;
      const refAt = Math.max(Date.now(), (_a = room.lastMessageAt) != null ? _a : 0, (_b = room.lastActive) != null ? _b : 0);
      setArchivedRooms((prev) => ({
        ...prev,
        [room.roomKey]: refAt
      }));
      if (!archiveView && (selectedRoom == null ? void 0 : selectedRoom.roomKey) === room.roomKey) {
        setSelectedRoom(null);
      }
    },
    [archiveView, selectedRoom == null ? void 0 : selectedRoom.roomKey]
  );
  const restoreRoom = useCallback3(
    (room) => {
      setArchivedRooms((prev) => {
        if (!prev[room.roomKey]) return prev;
        const next = { ...prev };
        delete next[room.roomKey];
        return next;
      });
      if (archiveView && (selectedRoom == null ? void 0 : selectedRoom.roomKey) === room.roomKey) {
        setSelectedRoom(null);
      }
    },
    [archiveView, selectedRoom == null ? void 0 : selectedRoom.roomKey]
  );
  const handleArchiveFromMenu = () => {
    if (!roomMenuRoom) return;
    archiveRoom(roomMenuRoom);
    handleCloseRoomMenu();
  };
  const handleRestoreFromMenu = () => {
    if (!roomMenuRoom) return;
    restoreRoom(roomMenuRoom);
    handleCloseRoomMenu();
  };
  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
  };
  const loadNote = useCallback3(async () => {
    if (!selectedRoom || !baseEndpoint || !tenantId) return;
    setNoteLoading(true);
    setNoteError(void 0);
    try {
      const response = await fetch(
        `${baseEndpoint}/rooms/${encodeURIComponent(tenantId)}/${encodeURIComponent(selectedRoom.roomId)}/note`,
        buildRequestInit()
      );
      if (!response.ok) throw new Error("Unable to fetch note");
      const body = await response.json();
      setNoteValue(body.note || "");
      setNoteBaseValue(body.note || "");
      setNoteMeta({ updatedAt: body.updatedAt, updatedBy: body.updatedBy });
    } catch (err) {
      console.error("SupportConsole: unable to load note", err);
      setNoteError("Unable to load note for this chat.");
      setNoteValue("");
      setNoteBaseValue("");
      setNoteMeta(void 0);
    } finally {
      setNoteLoading(false);
    }
  }, [selectedRoom, baseEndpoint, tenantId, buildRequestInit]);
  useEffect5(() => {
    if (selectedRoom) {
      loadNote();
    } else {
      setNoteValue("");
      setNoteBaseValue("");
      setNoteMeta(void 0);
    }
  }, [selectedRoom, loadNote]);
  const handleSaveNote = async () => {
    if (!selectedRoom || noteValue === noteBaseValue || !baseEndpoint || !tenantId) return;
    setNoteSaving(true);
    setNoteError(void 0);
    try {
      const response = await fetch(
        `${baseEndpoint}/rooms/${encodeURIComponent(tenantId)}/${encodeURIComponent(selectedRoom.roomId)}/note`,
        buildRequestInit({
          method: "PUT",
          headers: {
            "content-type": "application/json"
          },
          body: JSON.stringify({
            note: noteValue,
            updatedBy: (agentContext == null ? void 0 : agentContext.name) || defaultAgentName
          })
        })
      );
      if (!response.ok) throw new Error("Unable to save note");
      const body = await response.json();
      setNoteBaseValue(body.note || "");
      setNoteMeta({ updatedAt: body.updatedAt, updatedBy: body.updatedBy });
      setRooms(
        (prev) => prev.map(
          (room) => room.roomKey === body.roomKey ? {
            ...room,
            notePreview: body.note || void 0,
            noteUpdatedAt: body.updatedAt
          } : room
        )
      );
      setSelectedRoom(
        (current) => current && current.roomKey === body.roomKey ? {
          ...current,
          notePreview: body.note || void 0,
          noteUpdatedAt: body.updatedAt
        } : current
      );
    } catch (err) {
      console.error("SupportConsole: unable to save note", err);
      setNoteError("Unable to save note. Please try again.");
    } finally {
      setNoteSaving(false);
    }
  };
  const noteDirty = noteValue !== noteBaseValue;
  if (!endpoint || !tenantId) {
    return /* @__PURE__ */ jsx9(Alert2, { severity: "warning", sx: { mt: 4 }, children: "Set the chat endpoint and tenant ID to enable the support console." });
  }
  if (!gatePassword) {
    return /* @__PURE__ */ jsx9(Alert2, { severity: "error", sx: { mt: 4 }, children: "Configure a console password to access the support console." });
  }
  if (!authorized) {
    return /* @__PURE__ */ jsxs5(
      Box5,
      {
        component: "form",
        onSubmit: unlock,
        sx: { maxWidth: 360, mx: "auto", mt: 6, display: "flex", flexDirection: "column", gap: 2 },
        children: [
          /* @__PURE__ */ jsx9(Typography5, { variant: "h5", textAlign: "center", children: "Support Console" }),
          /* @__PURE__ */ jsx9(Typography5, { variant: "body2", color: "text.secondary", textAlign: "center", children: unlockMessage || "Enter the console password to continue." }),
          /* @__PURE__ */ jsx9(
            TextField3,
            {
              label: "Console password",
              type: "password",
              value: passwordAttempt,
              onChange: (event) => setPasswordAttempt(event.target.value),
              error: Boolean(passwordError),
              helperText: passwordError,
              autoComplete: "off"
            }
          ),
          /* @__PURE__ */ jsx9(Button5, { type: "submit", variant: "contained", children: "Unlock" })
        ]
      }
    );
  }
  const audienceChipColor = (room) => {
    if (isGuestRoom(room)) return "default";
    if (isAccountRoom(room)) return "primary";
    return "default";
  };
  const roomUpdatedLabel = (room) => {
    const ref = room.lastMessageAt || room.lastActive;
    if (!ref) return "No activity yet";
    return dayjs2(ref).fromNow();
  };
  return /* @__PURE__ */ jsx9(Box5, { sx: { width: "100%", maxWidth: "100%", boxSizing: "border-box", minHeight: 0 }, children: /* @__PURE__ */ jsxs5(Stack5, { direction: { xs: "column", lg: "row" }, spacing: 3, sx: { minHeight: 0, width: "100%" }, children: [
    /* @__PURE__ */ jsx9(
      Card2,
      {
        sx: {
          flex: { xs: "1 1 auto", lg: "1 1 360px" },
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0
        },
        children: /* @__PURE__ */ jsxs5(CardContent2, { sx: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }, children: [
          /* @__PURE__ */ jsxs5(Stack5, { direction: "row", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, children: [
            /* @__PURE__ */ jsxs5(Box5, { children: [
              /* @__PURE__ */ jsx9(Typography5, { variant: "h6", children: "Conversations" }),
              /* @__PURE__ */ jsxs5(Typography5, { variant: "body2", color: "text.secondary", children: [
                "Tenant: ",
                tenantId
              ] })
            ] }),
            /* @__PURE__ */ jsx9(IconButton4, { onClick: fetchRooms, disabled: loading, "aria-label": "Refresh rooms", children: loading ? /* @__PURE__ */ jsx9(CircularProgress, { size: 20 }) : /* @__PURE__ */ jsx9(RefreshIcon, {}) })
          ] }),
          error && /* @__PURE__ */ jsx9(Alert2, { severity: "error", sx: { mb: 2 }, children: error }),
          /* @__PURE__ */ jsxs5(Stack5, { direction: "row", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 1, flexWrap: "wrap", children: [
            /* @__PURE__ */ jsxs5(Tabs, { value: tab, onChange: handleTabChange, sx: { flex: 1, minWidth: 240 }, children: [
              /* @__PURE__ */ jsx9(Tab, { label: `Active (${tabCounts.active})`, value: "active" }),
              /* @__PURE__ */ jsx9(Tab, { label: `History (${tabCounts.history})`, value: "history" }),
              /* @__PURE__ */ jsx9(Tab, { label: `Created (${tabCounts.created})`, value: "created" })
            ] }),
            /* @__PURE__ */ jsx9(
              Button5,
              {
                size: "small",
                variant: archiveView ? "contained" : "outlined",
                startIcon: /* @__PURE__ */ jsx9(ArchiveOutlinedIcon, { fontSize: "small" }),
                onClick: () => setArchiveView((prev) => !prev),
                children: archiveView ? "Archived" : "Archive"
              }
            )
          ] }),
          tab !== "created" && /* @__PURE__ */ jsxs5(ToggleButtonGroup, { value: audienceFilter, exclusive: true, onChange: handleAudienceFilter, size: "small", sx: { mb: 2 }, children: [
            /* @__PURE__ */ jsx9(ToggleButton, { value: "all", children: "All" }),
            /* @__PURE__ */ jsx9(ToggleButton, { value: "guest", children: "Guests" }),
            /* @__PURE__ */ jsx9(ToggleButton, { value: "account", children: "Accounts" })
          ] }),
          /* @__PURE__ */ jsx9(
            TextField3,
            {
              size: "small",
              fullWidth: true,
              placeholder: "Search chats",
              value: searchQuery,
              onChange: (event) => setSearchQuery(event.target.value),
              sx: { mb: 2 },
              InputProps: {
                startAdornment: /* @__PURE__ */ jsx9(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx9(SearchIcon, { fontSize: "small", color: "action" }) })
              }
            }
          ),
          /* @__PURE__ */ jsxs5(Stack5, { spacing: 1, sx: { flex: 1, minHeight: 0 }, children: [
            /* @__PURE__ */ jsxs5(Stack5, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [
              /* @__PURE__ */ jsxs5(Typography5, { variant: "caption", color: "text.secondary", children: [
                "Showing ",
                visibleRooms.length,
                " of ",
                filteredRooms.length
              ] }),
              hasMoreRooms && /* @__PURE__ */ jsx9(
                Button5,
                {
                  size: "small",
                  onClick: () => setRoomsViewLimit((prev) => Math.min(filteredRooms.length, prev + ROOMS_VIEW_PAGE_SIZE)),
                  children: "Load more"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs5(List2, { dense: true, sx: { flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }, children: [
              visibleRooms.map((room) => {
                const archived = isRoomArchived(room);
                const lastMessagePreview = truncateSingleLine(room.lastMessagePreview, 160);
                const notePreview = truncateSingleLine(room.notePreview, 120);
                return /* @__PURE__ */ jsx9(
                  ListItem,
                  {
                    disablePadding: true,
                    secondaryAction: /* @__PURE__ */ jsx9(IconButton4, { edge: "end", "aria-label": "Chat actions", onClick: (event) => handleOpenRoomMenu(event, room), children: /* @__PURE__ */ jsx9(MoreVertIcon, { fontSize: "small" }) }),
                    children: /* @__PURE__ */ jsx9(
                      ListItemButton2,
                      {
                        selected: (selectedRoom == null ? void 0 : selectedRoom.roomKey) === room.roomKey,
                        onClick: () => handleSelectRoom(room),
                        sx: { pr: 6, minWidth: 0 },
                        children: /* @__PURE__ */ jsx9(
                          ListItemText2,
                          {
                            sx: { minWidth: 0 },
                            primary: /* @__PURE__ */ jsxs5(Stack5, { direction: "row", justifyContent: "space-between", alignItems: "center", spacing: 1, sx: { minWidth: 0 }, children: [
                              /* @__PURE__ */ jsx9(Typography5, { variant: "subtitle2", noWrap: true, sx: { minWidth: 0, flex: 1 }, children: resolveRoomDisplayTitle(room) }),
                              /* @__PURE__ */ jsxs5(Stack5, { direction: "row", alignItems: "center", spacing: 0.75, children: [
                                archived && /* @__PURE__ */ jsx9(Chip2, { label: "Archived", size: "small", variant: "outlined" }),
                                /* @__PURE__ */ jsx9(
                                  Chip2,
                                  {
                                    label: roomUpdatedLabel(room),
                                    size: "small",
                                    color: isCreatedRoom(room) ? "secondary" : audienceChipColor(room)
                                  }
                                )
                              ] })
                            ] }),
                            secondary: /* @__PURE__ */ jsxs5(Stack5, { spacing: 0.5, mt: 0.5, sx: { minWidth: 0 }, children: [
                              isCreatedRoom(room) ? /* @__PURE__ */ jsxs5(Fragment2, { children: [
                                resolveCreatedSecondaryLabel(room) && /* @__PURE__ */ jsx9(Typography5, { variant: "body2", color: "text.secondary", children: resolveCreatedSecondaryLabel(room) }),
                                /* @__PURE__ */ jsx9(Typography5, { variant: "caption", color: "text.secondary", children: resolveRoomSubtitle(room) })
                              ] }) : /* @__PURE__ */ jsxs5(Fragment2, { children: [
                                /* @__PURE__ */ jsx9(Typography5, { variant: "caption", color: "text.secondary", children: resolveRoomSubtitle(room) }),
                                lastMessagePreview && /* @__PURE__ */ jsx9(Typography5, { variant: "body2", color: "text.secondary", noWrap: true, sx: { minWidth: 0 }, children: lastMessagePreview })
                              ] }),
                              notePreview && /* @__PURE__ */ jsxs5(Stack5, { direction: "row", spacing: 0.5, alignItems: "center", children: [
                                /* @__PURE__ */ jsx9(NoteAltIcon, { fontSize: "inherit", color: "action" }),
                                /* @__PURE__ */ jsx9(Typography5, { variant: "caption", color: "text.secondary", noWrap: true, sx: { minWidth: 0 }, children: notePreview })
                              ] })
                            ] })
                          }
                        )
                      }
                    )
                  },
                  room.roomKey
                );
              }),
              !filteredRooms.length && !loading && /* @__PURE__ */ jsx9(Typography5, { color: "text.secondary", sx: { px: 2, py: 3 }, children: "No conversations match the selected filters." })
            ] })
          ] }),
          /* @__PURE__ */ jsxs5(
            Menu,
            {
              anchorEl: roomMenuAnchorEl,
              open: Boolean(roomMenuAnchorEl),
              onClose: handleCloseRoomMenu,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              transformOrigin: { vertical: "top", horizontal: "right" },
              children: [
                roomMenuRoom && !isRoomArchived(roomMenuRoom) && /* @__PURE__ */ jsxs5(MenuItem, { onClick: handleArchiveFromMenu, children: [
                  /* @__PURE__ */ jsx9(ArchiveOutlinedIcon, { fontSize: "small", sx: { mr: 1 } }),
                  "Archive chat"
                ] }),
                roomMenuRoom && isRoomArchived(roomMenuRoom) && /* @__PURE__ */ jsxs5(MenuItem, { onClick: handleRestoreFromMenu, children: [
                  /* @__PURE__ */ jsx9(RestoreIcon, { fontSize: "small", sx: { mr: 1 } }),
                  "Restore chat"
                ] })
              ]
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsxs5(Stack5, { spacing: 3, sx: { flex: { xs: "1 1 auto", lg: "2 1 560px" }, minWidth: 0, minHeight: 0 }, children: [
      /* @__PURE__ */ jsx9(Card2, { sx: { minHeight: 480 }, children: /* @__PURE__ */ jsx9(CardContent2, { sx: { display: "flex", flexDirection: "column", height: "100%" }, children: selectedRoom ? /* @__PURE__ */ jsx9(
        ChatProvider,
        {
          tenantId,
          endpoint,
          roomId: selectedRoom.roomId,
          authStrategy: "guest",
          role: "agent",
          displayName: (agentContext == null ? void 0 : agentContext.name) || defaultAgentName,
          storageKey: `${storageKeyPrefix}:${(agentContext == null ? void 0 : agentContext.storageKeySuffix) || "agent"}:${tenantId}:${selectedRoom.roomId}`,
          fetchOptions: providerFetchOptions,
          uploadFile,
          uploadAccept,
          maxUploadBytes,
          children: /* @__PURE__ */ jsx9(
            AgentRoomPanel,
            {
              room: selectedRoom,
              roomTitle: resolveRoomDisplayTitle(selectedRoom),
              onRoomLabel: setRoomLabel
            }
          )
        },
        selectedRoom.roomKey
      ) : /* @__PURE__ */ jsx9(Box5, { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", children: /* @__PURE__ */ jsx9(Typography5, { color: "text.secondary", children: "Select a room to join the conversation." }) }) }) }),
      /* @__PURE__ */ jsx9(Card2, { children: /* @__PURE__ */ jsx9(CardContent2, { children: /* @__PURE__ */ jsxs5(Stack5, { spacing: 2, children: [
        /* @__PURE__ */ jsxs5(Stack5, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [
          /* @__PURE__ */ jsx9(Typography5, { variant: "h6", children: "Shared notes" }),
          (noteMeta == null ? void 0 : noteMeta.updatedAt) && /* @__PURE__ */ jsxs5(Typography5, { variant: "caption", color: "text.secondary", children: [
            "Updated ",
            dayjs2(noteMeta.updatedAt).fromNow(),
            noteMeta.updatedBy ? ` by ${noteMeta.updatedBy}` : ""
          ] })
        ] }),
        /* @__PURE__ */ jsx9(
          TextField3,
          {
            value: noteValue,
            onChange: (event) => setNoteValue(event.target.value),
            placeholder: selectedRoom ? "Share context for other agents" : "Select a room to view notes",
            multiline: true,
            minRows: 3,
            maxRows: 6,
            disabled: !selectedRoom || noteLoading,
            error: Boolean(noteError),
            helperText: noteError
          }
        ),
        /* @__PURE__ */ jsx9(Stack5, { direction: "row", justifyContent: "flex-end", spacing: 1, children: /* @__PURE__ */ jsx9(
          Button5,
          {
            variant: "contained",
            onClick: handleSaveNote,
            disabled: !selectedRoom || !noteDirty || noteSaving,
            children: noteSaving ? "Saving..." : "Save note"
          }
        ) })
      ] }) }) })
    ] })
  ] }) });
};
var AgentRoomPanel = ({ room, roomTitle, onRoomLabel }) => {
  const {
    messages,
    participants,
    sendMessage,
    sendFile,
    sendTyping,
    connected,
    canUpload,
    uploading,
    uploadAccept,
    lastUploadError,
    you
  } = useChat();
  const [draft, setDraft] = useState5("");
  const [previewImage, setPreviewImage] = useState5(null);
  const listRef = useRef5(null);
  const fileInputRef = useRef5(null);
  const derivedPeerLabel = useMemo5(() => {
    let latest;
    messages.forEach((message) => {
      if (message.type === "system") return;
      if (message.from.role === "agent") return;
      if (!latest || message.at > latest.at) {
        latest = message;
      }
    });
    const fromMessage = normalizeLabel(latest == null ? void 0 : latest.from.name) || normalizeLabel(latest == null ? void 0 : latest.from.id);
    if (fromMessage && !isGenericLabel(fromMessage)) return fromMessage;
    const nonAgents = participants.filter((participant) => participant.role !== "agent");
    const fromPresence = nonAgents.find((participant) => normalizeLabel(participant.name) && !isGenericLabel(participant.name)) || nonAgents.find((participant) => normalizeLabel(participant.id) && !isGenericLabel(participant.id)) || nonAgents[0];
    const presenceLabel = normalizeLabel(fromPresence == null ? void 0 : fromPresence.name) || normalizeLabel(fromPresence == null ? void 0 : fromPresence.id);
    if (presenceLabel && !isGenericLabel(presenceLabel)) return presenceLabel;
    return void 0;
  }, [messages, participants]);
  useEffect5(() => {
    if (!onRoomLabel || !derivedPeerLabel) return;
    onRoomLabel(room.roomKey, derivedPeerLabel);
  }, [onRoomLabel, room.roomKey, derivedPeerLabel]);
  const uniqueParticipants = useMemo5(() => {
    const map = /* @__PURE__ */ new Map();
    const normalized = (value) => (value != null ? value : "").trim().toLowerCase();
    const isGenericName = (value) => value === "guest" || value === "agent" || value === "user";
    const keyFor = (participant) => {
      if ((you == null ? void 0 : you.id) && participant.id === you.id) return `you:${participant.id}`;
      if (participant.role === "guest") return `guest:${participant.id}`;
      const name = normalized(participant.name);
      if (name && !isGenericName(name)) {
        if (name.includes("@") || name.length >= 6) return `${participant.role}:${name}`;
      }
      return `${participant.role}:${participant.id}`;
    };
    const merge = (prev, next) => ({
      ...prev,
      ...next,
      typing: Boolean(prev.typing || next.typing)
    });
    participants.forEach((participant) => {
      const key = keyFor(participant);
      const existing = map.get(key);
      map.set(key, existing ? merge(existing, participant) : participant);
    });
    return Array.from(map.values());
  }, [participants, you == null ? void 0 : you.id]);
  useEffect5(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);
  const handleSend = () => {
    if (!draft.trim()) return;
    sendMessage(draft);
    setDraft("");
    sendTyping(false);
  };
  const handlePickFile = () => {
    var _a;
    if (!canUpload || uploading) return;
    (_a = fileInputRef.current) == null ? void 0 : _a.click();
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };
  const typingUsers = uniqueParticipants.filter((user) => user.role !== "agent" && user.typing);
  const typingLabel = typingUsers.length === 1 ? `${typingUsers[0].name || "Guest"} is typing\u2026` : typingUsers.length > 1 ? "Multiple users are typing\u2026" : "";
  const enforceDraftLimit = (nextValue) => {
    let next = nextValue;
    if (next.length > MAX_AGENT_MESSAGE_CHARS) next = next.slice(0, MAX_AGENT_MESSAGE_CHARS);
    const lines = next.split("\n");
    if (lines.length > MAX_AGENT_MESSAGE_LINES) next = lines.slice(0, MAX_AGENT_MESSAGE_LINES).join("\n");
    return next;
  };
  return /* @__PURE__ */ jsxs5(Stack5, { spacing: 2, sx: { height: "100%", minHeight: 0 }, children: [
    /* @__PURE__ */ jsxs5(Stack5, { direction: { xs: "column", sm: "row" }, justifyContent: "space-between", spacing: 1, children: [
      /* @__PURE__ */ jsxs5(Box5, { children: [
        /* @__PURE__ */ jsx9(Typography5, { variant: "h6", children: roomTitle || room.roomId || "Conversation" }),
        /* @__PURE__ */ jsxs5(Typography5, { variant: "body2", color: "text.secondary", children: [
          "Room ",
          room.roomId
        ] })
      ] }),
      /* @__PURE__ */ jsx9(Chip2, { label: connected ? "Online" : "Offline", color: connected ? "success" : "default", size: "small" })
    ] }),
    /* @__PURE__ */ jsx9(Divider3, {}),
    /* @__PURE__ */ jsx9(Stack5, { direction: "row", spacing: 1, flexWrap: "wrap", children: uniqueParticipants.map((participant) => {
      var _a;
      return /* @__PURE__ */ jsx9(
        Chip2,
        {
          label: `${participant.name || "Guest"}${participant.typing ? " \u2026" : ""}`,
          variant: participant.id === (you == null ? void 0 : you.id) ? "filled" : "outlined"
        },
        `${participant.role}:${participant.id}:${(_a = participant.name) != null ? _a : ""}`
      );
    }) }),
    /* @__PURE__ */ jsxs5(
      Box5,
      {
        ref: listRef,
        sx: {
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: "background.default",
          borderRadius: 1,
          p: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`
        },
        children: [
          !messages.length && /* @__PURE__ */ jsx9(Typography5, { color: "text.secondary", textAlign: "center", children: "No messages yet." }),
          /* @__PURE__ */ jsxs5(Stack5, { spacing: 1.5, children: [
            messages.map((message) => /* @__PURE__ */ jsx9(
              MessageBubble2,
              {
                message,
                selfId: you == null ? void 0 : you.id,
                selfName: you == null ? void 0 : you.name,
                onPreviewImage: (url, name) => setPreviewImage({ url, name })
              },
              message.id
            )),
            typingLabel && /* @__PURE__ */ jsx9(Typography5, { variant: "caption", color: "text.secondary", children: typingLabel })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx9(
      TextField3,
      {
        fullWidth: true,
        multiline: true,
        maxRows: 4,
        minRows: 1,
        placeholder: "Type a reply",
        value: draft,
        onChange: (event) => {
          const next = enforceDraftLimit(event.target.value);
          setDraft(next);
          sendTyping(next.trim().length > 0);
        },
        onKeyDown: handleKeyDown,
        helperText: `${draft.length}/${MAX_AGENT_MESSAGE_CHARS}`,
        InputProps: {
          endAdornment: /* @__PURE__ */ jsxs5(Stack5, { direction: "row", spacing: 0.5, alignItems: "center", children: [
            /* @__PURE__ */ jsx9(UploadButton, { visible: canUpload, onClick: handlePickFile, uploading, mode: "icon" }),
            /* @__PURE__ */ jsx9(IconButton4, { onClick: handleSend, color: "primary", disabled: !draft.trim(), "aria-label": "Send message", children: /* @__PURE__ */ jsx9(SendIcon2, {}) })
          ] })
        }
      }
    ),
    /* @__PURE__ */ jsx9(UploadInput, { fileInputRef, enabled: canUpload, accept: uploadAccept, onSelectFile: sendFile }),
    /* @__PURE__ */ jsx9(UploadError, { message: lastUploadError }),
    /* @__PURE__ */ jsx9(
      ImagePreviewDialog,
      {
        open: Boolean(previewImage),
        imageUrl: previewImage == null ? void 0 : previewImage.url,
        imageName: previewImage == null ? void 0 : previewImage.name,
        onClose: () => setPreviewImage(null)
      }
    )
  ] });
};
var normalizeName2 = (value) => (value || "").trim().toLowerCase();
var MessageBubble2 = ({ message, selfId, selfName, onPreviewImage }) => {
  var _a;
  const isSelf = message.from.id === selfId || Boolean(selfName && message.from.name && normalizeName2(selfName) === normalizeName2(message.from.name));
  const text = (_a = message.text) != null ? _a : "";
  const file = message.file;
  const fileMeta = formatFileSize2(file == null ? void 0 : file.size);
  const imageAttachment = isImageAttachment2(message);
  const isLong = text.length > 1200 || text.split("\n").length > 20;
  return /* @__PURE__ */ jsx9(Stack5, { alignItems: isSelf ? "flex-end" : "flex-start", children: /* @__PURE__ */ jsxs5(
    Box5,
    {
      sx: {
        maxWidth: "85%",
        px: 1.5,
        py: 1,
        borderRadius: 2,
        backgroundColor: (theme) => isSelf ? theme.palette.primary.main : theme.palette.grey[200],
        color: (theme) => isSelf ? theme.palette.primary.contrastText : theme.palette.text.primary,
        display: "inline-block",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        minWidth: 0,
        overflowX: "hidden",
        maxHeight: isLong ? "50vh" : void 0,
        overflowY: isLong ? "auto" : "visible"
      },
      children: [
        /* @__PURE__ */ jsx9(Typography5, { variant: "caption", sx: { opacity: 0.8 }, children: message.from.name || (isSelf ? selfName || "You" : "Guest") }),
        (file == null ? void 0 : file.url) && /* @__PURE__ */ jsx9(
          FileAttachment,
          {
            file,
            isImage: imageAttachment,
            fileMeta,
            onPreviewImage,
            containerSx: { mb: text ? 0.5 : 0 },
            linkSx: { color: "inherit" },
            metaSx: { opacity: 0.75 }
          }
        ),
        text && /* @__PURE__ */ jsx9(Typography5, { variant: "body2", sx: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word" }, children: text }),
        /* @__PURE__ */ jsx9(Typography5, { variant: "caption", sx: { opacity: 0.6, display: "block", mt: 0.5 }, children: dayjs2(message.at).format("MMM D, h:mm A") })
      ]
    }
  ) });
};
export {
  ChatProvider,
  CreatedChatsPanel,
  FileAttachment,
  FloatingChat,
  SupportConsole,
  UploadButton,
  UploadError,
  UploadInput,
  useChat
};
//# sourceMappingURL=index.js.map